import { useTranslation } from 'react-i18next'

import { Badge } from '~/components/common/Badge'
import type { CivilDate } from '~/models/civilDate'
import { expiryStatus } from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type Props = {
  validFrom: CivilDate
  validTo: CivilDate | undefined
  today: CivilDate
}

/**
 * 一覧のステータス列に表示する有効 / 終了 / 適用前バッジ。
 * 'active' のときは「有効」バッジを表示する
 * (design/admin/occto.html の一覧テーブル、有効行の .badge-ok 準拠)。
 */
export const ExpiredBadge = ({
  validFrom,
  validTo,
  today,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const status = expiryStatus(validFrom, validTo, today)

  if (status === 'expired') {
    // 竹モック EquipmentRenewable.razor の re-badge-expired 準拠で neutral
    // (終了は「失敗」ではなく「終端で良否を問わない終了状態」)。
    // 表示文言は spec occto-master/index.md の「失効」ではなく「終了」
    // (design/admin/occto.html の STATUS_BADGE と同じ 2026-08-05 の Des 判断。
    //  項目名「適用終了日」と語を揃え、罰則的なニュアンスを避ける)。
    //
    // **spec は未追従**: occto-master 配下の spec 8 ファイルは 3 区分を
    // 「有効 / 適用前 / 失効」で確定済み (issue #3795) で、 本変更はその確定事項に
    // 反する。 8080 側も「spec 追従の可否は issue #4887 で PdM 確認中」と保留を
    // 明示している。 PdM 判断が出たら spec を更新し、 本注記を削除する。
    // ステータス値・i18n キー (expired) は spec / 竹モックとの対応を保つため据え置く。
    return (
      <Badge status="neutral">
        {t('page:adminOcctoMaster.common.status.expired')}
      </Badge>
    )
  }
  if (status === 'future') {
    return (
      <Badge status="info">
        {t('page:adminOcctoMaster.common.status.future')}
      </Badge>
    )
  }
  return (
    <Badge status="positive">
      {t('page:adminOcctoMaster.common.status.active')}
    </Badge>
  )
}
